import { Pegasus } from 'corso-types';
import { match, P } from 'ts-pattern';
import { crewOrderContext } from '~/app/features/order-middleware/orderMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import api from '~/utils/api';
import { makeAction, makeLoader, redirect } from '~/utils/routing';

type Money = { amount: string };

function computeDelta(originalTotal: Money, calculatedTotal: Money) {
  const delta = Number(calculatedTotal.amount) - Number(originalTotal.amount);
  const direction = match(delta)
    .with(
      P.when((d) => d > 0),
      () => 'owe' as const,
    )
    .with(
      P.when((d) => d < 0),
      () => 'refund' as const,
    )
    .otherwise(() => 'none' as const);
  return { delta, direction };
}

export const { action, useActionResult } = makeAction(
  async ({ context }, appStore) => {
    const settings = context.get(settingsContext);
    const order = context.get(crewOrderContext);

    const { orderEdit } = appStore.getState();

    if (orderEdit.step !== 'reviewing') {
      return redirect('../');
    }

    const { step, ...body } = orderEdit;
    return api
      .commitOrderEdit({
        params: {
          storefrontId: settings.storefrontId,
          storeOrderId: order.id,
        },
        body: { ...body, ok: true },
      })
      .then((resp) => {
        if (resp.ok) {
          appStore.dispatch(appStore.actions.orderEditCommit());
        }
        if (
          'additionalPaymentCollectionUrl' in resp &&
          resp.additionalPaymentCollectionUrl
        ) {
          // clear the order
          appStore.dispatch(appStore.actions.invalidateOrder());
          // ? React Router Redirect cannot escape iFrame
          if (window.top) {
            window.top.location.href = resp.additionalPaymentCollectionUrl;
          } else {
            window.location.href = resp.additionalPaymentCollectionUrl;
          }
        }
        return resp;
      });
  },
);

export const { loader, useLoaderData } = makeLoader(({ context }, appStore) => {
  const { orderEdit } = appStore.getState();
  const settings = context.get(settingsContext);
  const order = context.get(crewOrderContext);

  if (orderEdit.step !== 'reviewing') return redirect('../');

  const { step, ...data } = orderEdit;

  const base = {
    currentAddress: order.shippingAddress,
    currentEmail: order.email,
    lineItems: order.lineItems,
    headline: Pegasus.OrderEditDisplayContent[data.editType].name,
    useStoreCreditRefund: settings.orderEditing.useStoreCreditRefund,
  };

  // editTypes whose stagedEdits carry totals get a precomputed delta so
  // confirm components don't redo the math.
  if (
    data.editType === 'MODIFY_QUANTITY' ||
    data.editType === 'UPDATE_SHIPPING_LINE' ||
    data.editType === 'ADD_DISCOUNT_CODE'
  ) {
    return {
      ...data,
      ...base,
      originalShippingTitle: order.shippingTitle,
      originalShippingPrice: order.shippingPrice,
      ...computeDelta(
        data.stagedEdits.originalTotal,
        data.stagedEdits.calculatedTotal,
      ),
    };
  }

  // Address edits only carry totals when tax recalculation ran; the recreate
  // flow always fully refunds to the original payment method.
  if (data.editType === 'UPDATE_SHIPPING_ADDRESS') {
    const { originalTotal, calculatedTotal } = data.stagedEdits;
    return {
      ...data,
      ...base,
      useStoreCreditRefund: false,
      ...(originalTotal && calculatedTotal
        ? computeDelta(originalTotal, calculatedTotal)
        : { delta: 0, direction: 'none' as const }),
    };
  }

  return { ...data, ...base };
});
